'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import PropTypes from 'prop-types';

import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  fetchModalities,
} from './risService';
import { getHeaderConfig } from '../Users/Users';
import TemplatesTab from './components/TemplatesTab';

const ITEMS_PER_PAGE = 10;

function PaginationControls({
  totalItems,
  currentPage,
  setCurrentPage,
}: {
  totalItems: number;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}) {
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  if (totalPages <= 1) return null;
  return (
    <div className="flex justify-between items-center mt-4 text-sm text-white">
      <span className="text-primary-light">
        Mostrando {Math.min(totalItems, (currentPage - 1) * ITEMS_PER_PAGE + 1)}–
        {Math.min(totalItems, currentPage * ITEMS_PER_PAGE)} de {totalItems}
      </span>
      <div className="flex gap-2">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(p => p - 1)}
          className="px-3 py-1 bg-secondary-dark rounded disabled:opacity-50 hover:bg-primary-light hover:text-black transition-colors"
        >
          Anterior
        </button>
        <span className="px-3 py-1">Página {currentPage} de {totalPages}</span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(p => p + 1)}
          className="px-3 py-1 bg-secondary-dark rounded disabled:opacity-50 hover:bg-primary-light hover:text-black transition-colors"
        >
          Siguiente
        </button>
      </div>
    </div>
  );
}

function TemplatesPanel({ servicesManager }) {
  const [templates, setTemplates] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', modality: '', contentHtml: '' });

  // CRUD State
  const [editingItem, setEditingItem] = useState(null); // { type: 'template', data: any }
  const [isEditing, setIsEditing] = useState(false);

  const { t } = useTranslation();
  const { show } = useModal();
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();

  const { menuOptions } = getHeaderConfig({
    servicesManager,
    t,
    show,
    navigate,
    appConfig,
  });

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [tpls, mods] = await Promise.all([
        fetchTemplates().catch(() => []),
        fetchModalities().catch(() => []),
      ]);
      setTemplates(tpls);
      setModalities(mods);
    } catch (error) {
      toast.error('Error cargando plantillas: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const fuzzySearch = useCallback(
    (item: any) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const name = (item.name || '').toLowerCase();
      const modality = (item.modality || '').toLowerCase();
      const content = (item.contentHtml || '').toLowerCase();
      return name.includes(q) || modality.includes(q) || content.includes(q);
    },
    [searchQuery]
  );

  const paginate = useCallback(
    (items: any[]) => {
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    },
    [currentPage]
  );

  const Paginator = useCallback(
    ({ totalItems }: { totalItems: number }) => (
      <PaginationControls
        totalItems={totalItems}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    ),
    [currentPage]
  );

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await createTemplate(newTemplate);
      toast.success('Plantilla creada');
      setIsCreatingTemplate(false);
      setNewTemplate({ name: '', modality: '', contentHtml: '' });
      const data = await fetchTemplates();
      setTemplates(data);
    } catch (err) {
      toast.error('Error: ' + (err as any).message);
    }
  };

  const handleEdit = (type, data) => {
    setEditingItem({ type, data });
    setIsEditing(true);
    if (type === 'template') {
      setNewTemplate({ ...data });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingItem(null);
    setNewTemplate({ name: '', modality: '', contentHtml: '' });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    try {
      const { type, data } = editingItem;
      const id = data._id;
      if (type === 'template') {
        await updateTemplate(id, newTemplate);
      }
      toast.success('Actualizado correctamente');
      handleCancelEdit();
      const updatedTemplates = await fetchTemplates();
      setTemplates(updatedTemplates);
    } catch (err) {
      toast.error('Error al actualizar: ' + (err as any).message);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('¿Está seguro de eliminar esta plantilla?')) return;
    try {
      if (type === 'template') {
        await deleteTemplate(id);
      }
      toast.success('Eliminado correctamente');
      const updatedTemplates = await fetchTemplates();
      setTemplates(updatedTemplates);
    } catch (err) {
      toast.error('Error al eliminar: ' + (err as any).message);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-black overflow-hidden">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={true}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="flex flex-1 bg-black text-white font-sans overflow-hidden sm:pl-20 pb-16 sm:pb-0">
        <main className="flex-1 flex flex-col min-w-0 bg-black">
          {/* Header Superior del Contenido */}
          <header className="flex-shrink-0 bg-black/80 backdrop-blur-md border-b border-secondary-dark flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 md:px-6 md:h-16 z-10 w-full animate-fade-in-up">
            <h1 className="text-xl font-bold flex items-center gap-2">
              Plantillas de Informe
            </h1>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              <div className="relative w-full sm:max-w-sm">
                <input
                  type="text"
                  placeholder="Buscar plantilla..."
                  className="w-full bg-primary-dark/80 border border-secondary-dark text-white rounded-full px-4 py-2 pl-10 text-sm focus:border-primary-light focus:outline-none transition-all placeholder:text-gray-500"
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                />
                <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
              </div>
              <button
                onClick={loadData}
                className="bg-secondary-dark hover:bg-gray-700 flex items-center justify-center rounded-full px-4 py-2 text-white text-sm transition-colors border border-gray-600 font-medium whitespace-nowrap"
              >
                Refrescar
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-auto p-4 md:p-6 custom-scrollbar relative">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-white text-sm">Cargando plantillas...</span>
              </div>
            ) : (
              <TemplatesTab
                templatesList={templates}
                fuzzySearch={fuzzySearch}
                paginate={paginate}
                isCreating={isCreatingTemplate}
                setIsCreating={setIsCreatingTemplate}
                isEditing={isEditing}
                editingItem={editingItem}
                handleCreate={handleCreateTemplate}
                handleUpdate={handleUpdate}
                handleCancelEdit={handleCancelEdit}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                newItemState={newTemplate}
                setNewItemState={setNewTemplate}
                PaginationControls={Paginator}
                modalities={modalities}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

TemplatesPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default TemplatesPanel;
